import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Alert, AlertTitle, Box, CircularProgress } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import api from '../../services/api';
import { BULK_UPLOAD_MAX_QUESTIONS } from '../../config/Config';

const DB_FIELDS = ['question_text','board','academic_year','difficulty','question_type','options','correct_answer','answer_evaluation','subject','topic','exam_mode'];

const QuestionUploadDialog = ({
  open,
  onClose,
  subjects,
  allTopics,
  boards,
  academicYears,
  refreshQuestions,
  onSuccess,
  isChampions = false, // Champions role specific behavior
}) => {
  const [uploadFile, setUploadFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [errorsByRow, setErrorsByRow] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    // reset state on every open
    setUploadFile(null);
    setPreviewRows([]);
    setErrorsByRow({});
    setError('');
    setSuccess('');
    setIsUploading(false);
    
  }, [open]);

  // Close handler without refresh (for normal close)
  const handleCloseWithoutRefresh = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose?.();
  };

  // Close handler with refresh (only for successful upload)
  const handleCloseWithRefresh = () => {
    onClose?.();
    refreshQuestions?.();
  };

  const parseOptions = (opt) => {
    if (Array.isArray(opt)) return opt;
    if (typeof opt !== 'string') return [];
    const trimmed = opt.trim();
    if (!trimmed) return [];
    try { return JSON.parse(trimmed); } catch { return trimmed.split(',').map(s=>s.trim()).filter(Boolean); }
  };

  const canonicalize = (text) => {
    if (text == null) return '';
    let s = String(text);
    try { s = s.normalize('NFKC'); } catch {}
    const fixes = [
      ['\u00c2\u00b0', '\u00b0'],
      ['\u00e2\u0082\u00b9', '₹'],
      ['\u00c2\u00a0', ' '],
    ];
    fixes.forEach(([bad, good]) => { s = s.replace(new RegExp(bad, 'g'), good); });
    return s.replace(/[\s\u00A0]+/g, ' ').trim().toLowerCase();
  };

  const keyFrom = (item) => {
    const q = canonicalize(item.question_text || '');
    const opts = JSON.stringify((Array.isArray(item.options)? item.options : parseOptions(item.options||''))
      .map(o=>canonicalize(o)).sort());
    const ans = canonicalize(item.correct_answer || '');
    return `${q}||${opts}||${ans}`;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadFile(file);
    setError('');
    setSuccess('');
    setErrorsByRow({});
    
    // Parse CSV/XLSX
    const reader = new FileReader();
    reader.onload = (ev) => {
      const data = new Uint8Array(ev.target.result);
      const wb = require('xlsx').read(data, { type: 'array' });
      const sh = wb.Sheets[wb.SheetNames[0]];
      const raw = require('xlsx').utils.sheet_to_json(sh, { header: 1 });
      if (!raw.length) { setPreviewRows([]); return; }
      const header = raw[0].map((h)=>String(h||'').trim());
      const rows = raw.slice(1).map((arr, idx)=>{
        const obj = {};
        header.forEach((h,i)=>{ obj[h] = arr[i]; });
        obj.id = idx; // preview index as id
        return obj;
      });
      // Just populate preview; do not validate until the user clicks Validate
      setPreviewRows(rows);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleValidateWithServer = async () => {
    if (!uploadFile) return;
    
    setIsUploading(true);
    setError('');
    setSuccess('');
    
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      
      const res = await api.post('/exams/questions/upload/', fd, { headers: { 'Content-Type':'multipart/form-data' } });
      
      if (res.data.errors && res.data.errors.length) {
        const newErrors = {};
        res.data.errors.forEach(err => {
          let idx = null;
          if (Number.isInteger(err.rowIndex)) idx = err.rowIndex;
          if (idx == null && Number.isInteger(err.row)) { const c = err.row - 2; if (c>=0 && c<previewRows.length) idx = c; }
          if (idx == null) {
            const sig = (s)=>String(s??'').trim().toLowerCase();
            const m = previewRows.findIndex(r => sig(r.question_text)===sig(err.question_text) && sig(r.correct_answer)===sig(err.correct_answer));
            if (m !== -1) idx = m;
          }
          if (idx != null) {
            const isDuplicate = /duplicate/i.test(err.error||'');
            newErrors[idx] = { ...(newErrors[idx]||{}), backend_error: err.error, _duplicate_db: isDuplicate };
          }
        });
        setErrorsByRow(newErrors);
        
        // Create detailed error message with specific errors
        const errorMessages = [];
        const duplicateCount = Object.values(newErrors).filter(err => err._duplicate_db).length;
        const otherErrorCount = Object.values(newErrors).filter(err => !err._duplicate_db).length;
        
        if (duplicateCount > 0) {
          errorMessages.push(`${duplicateCount} duplicate question${duplicateCount > 1 ? 's' : ''} skipped`);
        }
        
        if (otherErrorCount > 0) {
          // Get unique error types for other errors
          const uniqueErrors = [...new Set(Object.values(newErrors)
            .filter(err => !err._duplicate_db)
            .map(err => err.backend_error)
            .filter(Boolean)
          )];
          
          if (uniqueErrors.length > 0) {
            errorMessages.push(`${otherErrorCount} question${otherErrorCount > 1 ? 's' : ''} had issues: ${uniqueErrors.join(', ')}`);
          }
        }
        
        if (errorMessages.length > 0) {
          setError(errorMessages.join('. ') + '. Please fix the highlighted rows and resubmit.');
        } else {
          setError('Some questions had issues. Please fix the highlighted rows and resubmit.');
        }
        setSuccess('');
        setIsUploading(false);
        return; // keep dialog open
      }
      // No errors => created. Notify and close+refresh
      setIsUploading(false);
      onSuccess?.('All questions added successfully');
      handleCloseWithRefresh();
    } catch (err) {
      setIsUploading(false);
      const status = err?.response?.status;
      const dataDetail = err?.response?.data?.detail || err?.response?.data?.message;
      if (status === 413) {
        setError(
          'File is too large for the server to accept. Please upload a smaller file or fewer questions (max ' +
          BULK_UPLOAD_MAX_QUESTIONS +
          ' per file). If you need to upload more, split the file or ask your administrator to increase the server upload limit.'
        );
      } else if (dataDetail) {
        setError(typeof dataDetail === 'string' ? dataDetail : 'Upload failed. Please try again.');
      } else {
        setError('Upload failed. Please try again.');
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseWithoutRefresh} maxWidth="lg" fullWidth>
      <DialogTitle>Upload Questions (CSV / Excel)</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ mb:1, display:'block' }}>
          You can upload up to {BULK_UPLOAD_MAX_QUESTIONS} questions at a time.
        </Typography>
        <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} fullWidth>
          Choose File
          <input type="file" accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" hidden onChange={handleFileChange}/>
        </Button>
        {uploadFile && <Typography mt={1}>{uploadFile.name}</Typography>}

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError('')}>
            <AlertTitle>Upload Issues</AlertTitle>
            {error}
          </Alert>
        )}
        {success && <Alert severity="success" sx={{ mt: 2, mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {previewRows.length>0 && (
          <>
            <Typography variant="caption" color="error" sx={{ display:'block', mb:1 }}>
              Rows highlighted had issues (e.g., validation errors, duplicates). Please fix/change, remove the problematic questions from the file and resubmit
            </Typography>
            <Box mt={1} sx={{ height: '60vh', minHeight: 400, maxHeight: '80vh' }}>
              <DataGrid
                rows={previewRows.map(row => ({
                  ...row,
                  error_message: errorsByRow[row.id]?.backend_error || ''
                }))}
                columns={[
                  ...DB_FIELDS.map((f)=>({
                    field: f,
                    headerName: f.replace(/_/g,' ').toUpperCase(),
                    flex: 1,
                    minWidth: 130,
                    cellClassName: (p)=>{
                      const e = errorsByRow[p.row.id];
                      return e && (e[f] || e._duplicate || e._duplicate_db || e.backend_error) ? 'error-cell' : '';
                    }
                  })),
                  {
                    field: 'error_message',
                    headerName: 'ERROR',
                    width: 200,
                    cellClassName: (p) => p.value ? 'error-cell' : '',
                    renderCell: (params) => {
                      if (params.value) {
                        return (
                          <Typography variant="caption" color="error" sx={{ fontSize: '0.75rem' }}>
                            {params.value}
                          </Typography>
                        );
                      }
                      return '';
                    }
                  }
                ]}
                pageSize={10}
                rowsPerPageOptions={[5,10,25,50]}
                getRowId={(row)=>row.id}
                getRowClassName={(p)=>{
                  const e = errorsByRow[p.id];
                  return e && Object.keys(e).length>0 ? 'row-error' : '';
                }}
                sx={{
                  '& .row-error': { backgroundColor:'#ffebee', '&:hover':{ backgroundColor:'#ffcdd2' } },
                  '& .error-cell': { color:'error.main', fontWeight:'bold', backgroundColor:'#ffcdd2' },
                  '& .MuiDataGrid-cell': { borderBottom:'1px solid #e0e0e0' },
                }}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseWithoutRefresh}>Close</Button>
        {previewRows.length>0 && (
          <>
            <Button 
              variant="outlined" 
              color="info" 
              onClick={handleValidateWithServer} 
              disabled={isUploading}
              sx={{ ml:1 }}
              startIcon={isUploading ? <CircularProgress size={16} /> : null}
            >
              {isUploading ? 'Processing...' : 'Validate and submit'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuestionUploadDialog;


